/**
 * 사진 자르기·회전 계산.
 *
 * 왜 한 파일에 모으나
 * -----------------
 * 미리보기(InstagramCropper)와 저장(PhotoEdit)이 각자 계산하면 조금만 달라도
 * "화면에서 본 것과 저장된 사진이 다르다"가 된다. 그래서 두 곳이 같은 함수를
 * 쓰게 하고, 그리는 것도 drawCrop 하나로 처리한다.
 */

export interface CropTransform {
  /** 자르기 창 크기 (화면 CSS px) */
  cropW: number
  cropH: number
  /** 화면에서 사진을 끌어 옮긴 양 (CSS px) */
  pos: { x: number; y: number }
  /** 사용자가 추가로 준 확대 (1 = 없음) */
  zoom: number
  /** 회전 각도(도). +는 시계방향 */
  rotation: number
  /** 원본 사진 픽셀 크기 */
  naturalW: number
  naturalH: number
}

/** 자르기 창 크기를 담을 컨테이너와 비율로부터 구한다 */
export function cropSizeFor(
  containerW: number,
  containerH: number,
  aspect: number
): { w: number; h: number } {
  if (containerW === 0 || containerH === 0) return { w: 0, h: 0 }
  const containerAspect = containerW / containerH
  return aspect > containerAspect
    ? { w: containerW, h: containerW / aspect }
    : { w: containerH * aspect, h: containerH }
}

/**
 * 회전한 사진이 자르기 창을 빈틈없이 덮는 최소 배율 (CSS px / 원본 px).
 *
 * 각도가 커질수록 더 키워야 모서리에 빈 곳이 안 생긴다.
 * 45°에서 가장 많이 키워야 하고, 그때 사진의 40% 남짓이 잘려나간다.
 */
export function coverScale(t: CropTransform): number {
  if (t.naturalW === 0 || t.naturalH === 0) return 0
  const r = (t.rotation * Math.PI) / 180
  const c = Math.abs(Math.cos(r))
  const s = Math.abs(Math.sin(r))
  return Math.max(
    (t.cropW * c + t.cropH * s) / t.naturalW,
    (t.cropW * s + t.cropH * c) / t.naturalH
  )
}

/**
 * 자르기 창이 늘 사진으로 덮이도록 이동량을 가둔다.
 *
 * 회전한 사진을 감싸는 '바깥 사각형'으로 가두면 안 된다. 그 사각형에는 사진이
 * 없는 모서리 삼각형이 들어 있어서, 모서리 쪽으로 끌면 빈 곳이 드러난다.
 * (기하 계산만 떼어 79,821가지를 넣어보니 91.3%에서 빈 곳이 생겼다)
 *
 * 그래서 사진의 좌표계로 옮겨서 본다. 화면에서 축에 나란한 자르기 창은 사진
 * 좌표계에서 기울어진 사각형이 되는데, 그것이 사진 사각형 안에 들어가는 것은
 * 그 기울어진 사각형의 바깥 사각형이 들어가는 것과 같다(필요충분).
 */
export function clampPos(t: CropTransform): { x: number; y: number } {
  const scale = coverScale(t) * t.zoom
  const dw = t.naturalW * scale
  const dh = t.naturalH * scale
  const r = (t.rotation * Math.PI) / 180
  const c = Math.abs(Math.cos(r))
  const s = Math.abs(Math.sin(r))

  // 사진 좌표계에서 자르기 창이 차지하는 반너비/반높이
  const halfX = (t.cropW * c + t.cropH * s) / 2
  const halfY = (t.cropW * s + t.cropH * c) / 2
  const maxX = Math.max(0, dw / 2 - halfX)
  const maxY = Math.max(0, dh / 2 - halfY)

  // 화면 이동량을 사진 좌표계로 돌린다
  const co = Math.cos(r)
  const si = Math.sin(r)
  let lx = -(t.pos.x * co + t.pos.y * si)
  let ly = t.pos.x * si - t.pos.y * co
  lx = Math.min(maxX, Math.max(-maxX, lx))
  ly = Math.min(maxY, Math.max(-maxY, ly))

  // 다시 화면 좌표계로
  return {
    x: -(co * lx - si * ly),
    y: -(si * lx + co * ly),
  }
}

/**
 * 자르기 결과를 그린다.
 *
 * 자르기 창이 대상 캔버스의 (dx, dy, dw, dh) 자리에 온다고 보고 그린다.
 *  - 미리보기: 컨테이너 가운데의 자르기 창 자리 (배율 1)
 *  - 저장    : 캔버스 전체 (원본 해상도로 확대)
 * 두 경우가 같은 함수를 쓰므로 화면과 결과가 어긋나지 않는다.
 *
 * 호출부는 필요하면 미리 clip 해 둔다 (미리보기는 창 밖을 안 그려야 한다).
 */
export function drawCrop(
  ctx: CanvasRenderingContext2D,
  img: CanvasImageSource,
  t: CropTransform,
  dx: number,
  dy: number,
  dw: number,
  dh: number
): void {
  if (t.cropW === 0 || t.cropH === 0) return
  // 자르기 창 → 대상 크기 배율
  const k = dw / t.cropW
  const scale = coverScale(t) * t.zoom * k
  const imgW = t.naturalW * scale
  const imgH = t.naturalH * scale

  ctx.save()
  ctx.translate(dx + dw / 2 + t.pos.x * k, dy + dh / 2 + t.pos.y * k)
  ctx.rotate((t.rotation * Math.PI) / 180)
  ctx.drawImage(img, -imgW / 2, -imgH / 2, imgW, imgH)
  ctx.restore()
}

/**
 * 저장할 때 쓸 출력 크기(원본 해상도 기준).
 *
 * 자르기 창 1 CSS px 은 원본 1/scale px 이므로, 그만큼 키워 내보낸다.
 * 회전으로 확대된 만큼은 자연히 반영된다.
 */
export function outputSizeFor(
  t: CropTransform,
  maxSide: number
): { w: number; h: number } {
  const scale = coverScale(t) * t.zoom
  if (scale <= 0) return { w: 1, h: 1 }
  let w = Math.max(1, Math.round(t.cropW / scale))
  let h = Math.max(1, Math.round(t.cropH / scale))
  if (w > maxSide || h > maxSide) {
    const r = Math.min(maxSide / w, maxSide / h)
    w = Math.max(1, Math.floor(w * r))
    h = Math.max(1, Math.floor(h * r))
  }
  return { w, h }
}
